import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { ApiError } from "@/api/errors";
import { toModifiedProcessModelId } from "@/api/identifiers";
import { qk } from "@/api/query-keys";
import { CASE_CONFIG, type CaseConfig } from "@/domain/case/case-config";

import type { CaseRef } from "./use-case-detail";

/** 案件詳細でその場でできる操作。 */
export type CaseAction = {
  /** 起案タスク（＝下書き・差し戻しの再入力）か、それ以外（＝判定）か。 */
  readonly kind: "draft" | "review";
  readonly taskGuid: string;
  /** 画面に出すステップ名（BPMN の name）。 */
  readonly stepLabel: string | null;
  /**
   * フォームが紐付いていない（backend が 400 `missing_form_file`）。
   * 操作はできないが、理由を出さないと「なぜ承認できないのか」が分からない。
   */
  readonly formMissing: boolean;
};

/** task-info の行（必要な分だけ）。 */
type SpiffTaskInfoRow = {
  readonly guid?: string | null;
  readonly bpmn_identifier?: string | null;
  readonly bpmn_name?: string | null;
  readonly state?: string | null;
  readonly typename?: string | null;
};

/** 人が完了させるタスクの型（自動処理は対象外）。 */
const HUMAN_TASK_TYPES = new Set(["UserTask", "ManualTask"]);

/**
 * 「この案件で今の自分ができる操作」。無ければ null。
 *
 * 判定は `can_complete`（backend が担当者・グループ・レーンを見て返す）だけで行う。
 * 起票者か承認者かという役割で分岐すると、モデルの作りごとに条件が増えていく。
 *
 * 手順は 2 段:
 *  1. `task-info` で開いている人手タスクとその BPMN id を得る（一覧を経由しないので
 *     詳細ページを直接開いても効く）
 *  2. そのタスクを取り、`can_complete` とフォームを見る
 *
 * `task-info` は図（`use-case-diagram`）でも取っているが、あちらは案件詳細と
 * まとめて 1 クエリにしているので共有していない。応答は小さく 30 秒キャッシュする。
 *
 * 開いている人手タスクが複数あるとき（並行ゲートウェイ）は**最初の 1 つ**を対象に
 * する。当リポジトリのモデルはいずれも人手タスクが同時に 1 つしか開かない。
 */
export function caseActionQueryOptions(
  client: ApiClient,
  ref: CaseRef,
  params: { readonly config?: CaseConfig } = {},
) {
  const config = params.config ?? CASE_CONFIG;
  const modifiedId = toModifiedProcessModelId(ref.processModelId);

  const fetchAction = async (): Promise<CaseAction | null> => {
    const rows = await fetchTaskInfo(client, modifiedId, ref.caseId);
    const open = rows.find(
      (row) =>
        row.state === "READY" &&
        typeof row.typename === "string" &&
        HUMAN_TASK_TYPES.has(row.typename),
    );

    const taskGuid = open?.guid ?? null;
    if (open === undefined || taskGuid === null || taskGuid === "") return null;

    const identifier = open.bpmn_identifier ?? "";
    const kind = config.draftTaskNames.includes(identifier)
      ? "draft"
      : "review";
    const stepLabel = emptyToNull(open.bpmn_name);

    try {
      const task = await client.get<{ readonly can_complete?: boolean | null }>(
        `/tasks/${ref.caseId}/${taskGuid}`,
        { query: { with_form_data: true } },
      );

      if (task.can_complete !== true) return null;

      return { kind, taskGuid, stepLabel, formMissing: false };
    } catch (error) {
      // フォーム未設定は「操作できない理由」として見せる。それ以外（権限・通信）は
      // 操作の入口を出さない（押しても通らないボタンを出さない）。
      if (isMissingForm(error)) {
        return { kind, taskGuid, stepLabel, formMissing: true };
      }
      return null;
    }
  };

  return queryOptions({
    queryKey: [...qk.processInstances.detail(modifiedId, ref.caseId), "action"],
    queryFn: fetchAction,
    staleTime: 30_000,
  });
}

export function useCaseAction(ref: CaseRef): UseQueryResult<CaseAction | null> {
  return useQuery(caseActionQueryOptions(useApiClient(), ref));
}

/**
 * 開いているタスクの一覧。取れなければ空で扱う（全案件モニタから関与していない
 * 案件を開くと 403 になる。図と同じ扱いで、操作は出さない）。
 */
async function fetchTaskInfo(
  client: ApiClient,
  modifiedId: string,
  caseId: number,
): Promise<readonly SpiffTaskInfoRow[]> {
  try {
    const response = await client.get<unknown>(
      `/process-instances/for-me/${modifiedId}/${caseId}/task-info`,
    );

    if (Array.isArray(response)) return response as SpiffTaskInfoRow[];

    return (
      (response as { readonly results?: readonly SpiffTaskInfoRow[] })
        ?.results ?? []
    );
  } catch {
    return [];
  }
}

function isMissingForm(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 400 &&
    error.errorCode === "missing_form_file"
  );
}

function emptyToNull(value: string | null | undefined): string | null {
  return value === null || value === undefined || value === "" ? null : value;
}
